import { useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Mail, AlertTriangle } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthForm, Field } from '@/components/auth/AuthForm'
import { useAuth } from '@/lib/auth'

// =============================================================================
// /forgot-password
//
// Single-purpose page: email field + Send button. Triggers
// supabase.auth.resetPasswordForEmail; Supabase Auth emails a recovery
// link (token_hash) that lands on the public /reset-password page where
// the user sets a new password — the token is only exchanged on submit.
//
// Lives as its own route (rather than inline on SignInPage) so the
// user's mental model is clean: "I forgot my password" is its own
// action, not a side mode of the sign-in form.
// =============================================================================

export function ForgotPasswordPage() {
  const { resetPassword } = useAuth()
  const location = useLocation()
  // Set by AuthErrorBoundary when an expired recovery link bounced the
  // user back here. We surface a specific banner so they understand
  // why they landed on /forgot-password instead of /account/security.
  const expired = Boolean((location.state as { expired?: boolean } | null)?.expired)
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: resetError } = await resetPassword(email.trim())
    setSubmitting(false)
    if (resetError) {
      setError(resetError.message)
      return
    }
    setSent(true)
  }

  return (
    <SiteLayout>
      <AuthCard
        title="Forgot Password"
        subtitle={
          sent
            ? 'If an account exists for that email, a reset link is on its way.'
            : 'Enter the email on your account and we\'ll send a reset link.'
        }
        footer={
          <>
            Remembered it?{' '}
            <Link
              to="/sign-in"
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Sign in
            </Link>
            .
          </>
        }
      >
        {expired && !sent ? (
          <div
            role="status"
            className={[
              'mb-4 flex items-start gap-2.5',
              'rounded-[var(--radius-DEFAULT)] border border-[var(--color-signal)]/40',
              'bg-[var(--color-signal-soft)] px-3 py-2.5',
              'text-[12.5px] text-[var(--color-text)]',
            ].join(' ')}
          >
            <AlertTriangle size={14} className="text-[var(--color-signal)] shrink-0 mt-0.5" aria-hidden />
            <span>
              That reset link is expired or already used. Enter your email
              below and we&apos;ll send a fresh one.
            </span>
          </div>
        ) : null}
        {sent ? (
          <div className="flex flex-col items-center gap-4 py-2">
            <div
              aria-hidden
              className={[
                'inline-flex items-center justify-center w-14 h-14 rounded-full',
                'bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
              ].join(' ')}
            >
              <Mail size={26} strokeWidth={2} />
            </div>
            <p className="text-[13px] text-[var(--color-muted)] text-center">
              Check your inbox (and spam folder) for a message from{' '}
              <span className="font-[var(--font-mono)]">hello@geoborder.us</span>.
              The link expires in 1 hour.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false)
                setEmail('')
              }}
              className="text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-accent)] cursor-pointer transition-colors"
            >
              Send to a different email
            </button>
          </div>
        ) : (
          <AuthForm
            onSubmit={onSubmit}
            submitLabel="Send Reset Link"
            submitting={submitting}
            error={error}
          >
            <Field
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              placeholder="you@example.com"
            />
          </AuthForm>
        )}
      </AuthCard>
    </SiteLayout>
  )
}
